import { useState } from "react";
import { Loader2, Upload, X } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, formatApiError, imgSrc, STATUS, TYPES } from "@/lib/api";

const EMPTY = { name: "", city: "", type: "residence", status: "disponible", price: "", price_from: false, short_description: "", description: "", features: [], bedrooms: "", bathrooms: "", surface: "", images: [], featured: false };
const num = (v) => (v === "" || v == null ? null : Number(v));

const ImagesField = ({ images, onChange }) => {
  const [uploading, setUploading] = useState(false);
  const [url, setUrl] = useState("");
  const upload = async (e) => {
    const files = [...e.target.files];
    setUploading(true);
    try {
      const urls = [];
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        const { data } = await api.post("/upload", fd);
        urls.push(data.url);
      }
      onChange([...images, ...urls]);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setUploading(false); e.target.value = ""; }
  };
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-3">
        {images.map((src, i) => (
          <div key={src + i} className="relative h-20 w-28 rounded-sm overflow-hidden group" data-testid={`programme-image-${i}`}>
            <img src={imgSrc(src)} alt="" className="h-full w-full object-cover" />
            <button type="button" onClick={() => onChange(images.filter((_, j) => j !== i))} className="absolute top-1 right-1 bg-black/60 text-white rounded-full p-1" data-testid={`programme-image-remove-${i}`}><X className="h-3 w-3" /></button>
            {i === 0 && <span className="absolute bottom-0 inset-x-0 bg-[#C88A58] text-white text-[10px] text-center">Couverture</span>}
          </div>
        ))}
        <label className="h-20 w-28 border border-dashed border-[#2F2A28]/30 rounded-sm grid place-items-center cursor-pointer hover:border-[#C88A58] text-[#756D69] text-xs" data-testid="programme-image-upload-label">
          {uploading ? <Loader2 className="h-5 w-5 animate-spin" /> : <span className="flex flex-col items-center gap-1"><Upload className="h-4 w-4" />Téléverser</span>}
          <input type="file" accept="image/jpeg,image/png,image/webp" multiple className="hidden" onChange={upload} disabled={uploading} data-testid="programme-image-upload-input" />
        </label>
      </div>
      <div className="flex gap-2">
        <Input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="ou coller l'URL d'une image" data-testid="programme-image-url-input" />
        <button type="button" onClick={() => { if (url.trim()) { onChange([...images, url.trim()]); setUrl(""); } }} className="px-4 text-sm border rounded-sm hover:bg-[#F2EDE4]" data-testid="programme-image-url-add">Ajouter</button>
      </div>
    </div>
  );
};

export const ProgrammeFormDialog = ({ programme, onClose, onSaved }) => {
  const [f, setF] = useState(programme ? { ...EMPTY, ...programme, price: programme.price ?? "", bedrooms: programme.bedrooms ?? "", bathrooms: programme.bathrooms ?? "", surface: programme.surface ?? "" } : EMPTY);
  const [featuresText, setFeaturesText] = useState((programme?.features || []).join("\n"));
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    const body = { ...f, price: num(f.price), bedrooms: num(f.bedrooms), bathrooms: num(f.bathrooms), surface: String(f.surface).trim() || null, features: featuresText.split("\n").map((s) => s.trim()).filter(Boolean) };
    try {
      if (programme) await api.put(`/programmes/${programme.id}`, body);
      else await api.post("/programmes", body);
      toast.success(programme ? "Programme mis à jour" : "Programme créé");
      onSaved();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl w-[calc(100vw-1.5rem)] max-h-[90vh] overflow-y-auto p-5 sm:p-6" data-testid="programme-form-dialog">
        <DialogHeader><DialogTitle className="font-display text-3xl font-normal">{programme ? "Modifier le programme" : "Nouveau programme"}</DialogTitle><DialogDescription>Informations, prix et photos du programme.</DialogDescription></DialogHeader>
        <form onSubmit={submit} className="grid sm:grid-cols-2 gap-5" data-testid="programme-form">
          <div className="space-y-2"><Label>Nom</Label><Input required value={f.name} onChange={set("name")} data-testid="programme-name-input" /></div>
          <div className="space-y-2"><Label>Ville</Label><Input required value={f.city} onChange={set("city")} data-testid="programme-city-input" /></div>
          <div className="space-y-2"><Label>Type</Label>
            <Select value={f.type} onValueChange={(v) => setF({ ...f, type: v })}>
              <SelectTrigger data-testid="programme-type-select"><SelectValue /></SelectTrigger>
              <SelectContent>{Object.entries(TYPES).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-2"><Label>Statut</Label>
            <Select value={f.status} onValueChange={(v) => setF({ ...f, status: v })}>
              <SelectTrigger data-testid="programme-status-select"><SelectValue /></SelectTrigger>
              <SelectContent>{Object.entries(STATUS).map(([k, s]) => <SelectItem key={k} value={k}>{s.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-2"><Label>Prix (FCFA) — vide = « Prix sur demande »</Label><Input type="number" min="0" value={f.price} onChange={set("price")} data-testid="programme-price-input" /></div>
          <div className="space-y-2"><Label>Surface (m²) — ex. 150 ou « 200 à 300 »</Label><Input value={f.surface} onChange={set("surface")} data-testid="programme-surface-input" /></div>
          <div className="space-y-2"><Label>Chambres</Label><Input type="number" min="0" value={f.bedrooms} onChange={set("bedrooms")} data-testid="programme-bedrooms-input" /></div>
          <div className="space-y-2"><Label>Salles de bain</Label><Input type="number" min="0" value={f.bathrooms} onChange={set("bathrooms")} data-testid="programme-bathrooms-input" /></div>
          <div className="space-y-2 sm:col-span-2"><Label>Accroche</Label><Input value={f.short_description} onChange={set("short_description")} data-testid="programme-short-input" /></div>
          <div className="space-y-2 sm:col-span-2"><Label>Description</Label><Textarea rows={5} value={f.description} onChange={set("description")} data-testid="programme-description-input" /></div>
          <div className="space-y-2 sm:col-span-2"><Label>Prestations (une par ligne)</Label><Textarea rows={4} value={featuresText} onChange={(e) => setFeaturesText(e.target.value)} placeholder={"Piscine\nGarage\nTitre foncier"} data-testid="programme-features-input" /></div>
          <div className="space-y-2 sm:col-span-2"><Label>Photos (la première sert de couverture)</Label><ImagesField images={f.images} onChange={(images) => setF({ ...f, images })} /></div>
          <div className="flex items-center gap-3 sm:col-span-2"><Switch checked={f.price_from} onCheckedChange={(v) => setF({ ...f, price_from: v })} data-testid="programme-price-from-switch" /><Label>Afficher « À partir de » devant le prix</Label></div>
          <div className="flex items-center gap-3 sm:col-span-2"><Switch checked={f.featured} onCheckedChange={(v) => setF({ ...f, featured: v })} data-testid="programme-featured-switch" /><Label>Afficher sur la page d'accueil</Label></div>
          <div className="sm:col-span-2 flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="px-5 py-2.5 border rounded-full text-sm" data-testid="programme-form-cancel">Annuler</button>
            <button type="submit" disabled={saving} className="px-6 py-2.5 bg-[#C88A58] hover:bg-[#b57849] disabled:opacity-60 text-white rounded-full text-sm inline-flex items-center gap-2" data-testid="programme-form-submit">
              {saving && <Loader2 className="h-4 w-4 animate-spin" />} Enregistrer
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
